import { useQuery } from '@apollo/client';
import GET_DEFAULT_GROUP_SETTINGS from 'graph/group/get-default-group-settings-query';
import useProductCache from 'store/cache/product-cache';
import {
  BulkGroupEnrollmentActionInput,
  DefaultGroupSetting,
  GroupCreate,
  GroupEnrollmentAction,
  Student,
  Group,
  BulkLicenseActionInput,
  LicenseAction
} from 'types/schema-generated-types';

interface DefaultGroupSettings {
  getDefaultGroupSettings: (productCodes: string[]) => DefaultGroupSetting[];
  getProductCurriculumDefaultGroupSettings: (
    productCurriculumIds: number[]
  ) => DefaultGroupSetting[];
  getDefaultGroup: (productCodes: string[], gradeLevel: string) => GroupCreate | null;
  getProductCurriculumDefaultGroup: (
    productCurriculumIds: number[],
    gradeLevel: string
  ) => GroupCreate | null;
  getGroupEnrollments: (
    classroomId: number,
    students: Student[],
    licenseAssignments: BulkLicenseActionInput[]
  ) => BulkGroupEnrollmentActionInput[];
  getGroupUnenrollments: (
    classroomId: number,
    students: Student[],
    licenseUnassignments: BulkLicenseActionInput[]
  ) => BulkGroupEnrollmentActionInput[];
}

const isClassroomEqual = (classroomId: number) => (x: Group) => x.classroomId === classroomId;

const useDefaultGroupSettings = (): DefaultGroupSettings => {
  const [{ findProductByProductCode }] = useProductCache();
  const { data } = useQuery(GET_DEFAULT_GROUP_SETTINGS);

  const defaultGroupSettings: (DefaultGroupSetting & {
    productIds: number[];
    productCurriculumIds: number[];
  })[] =
    data?.defaultGroupSettings?.map((defaultGroup: DefaultGroupSetting) => {
      const { productIds, productCurriculumIds } = defaultGroup.productCodes.reduce(
        (acc, c) => {
          const product = findProductByProductCode(c);
          if (!product) return acc;
          const productCurriculumIds = product.curriculums?.map(x => x.id) ?? [];

          return {
            productIds: [...acc.productIds, product.id],
            productCurriculumIds: [...acc.productCurriculumIds, ...productCurriculumIds]
          };
        },
        { productIds: [], productCurriculumIds: [] } as {
          productIds: number[];
          productCurriculumIds: number[];
        }
      );

      return {
        ...defaultGroup,
        productIds,
        productCurriculumIds,
        group: {
          appId: defaultGroup.group.appId,
          name: defaultGroup.group.name,
          groupCategory: {
            appId: defaultGroup.group.groupCategory?.appId,
            name: defaultGroup.group.groupCategory?.name
          }
        } as GroupCreate
      };
    }) ?? [];

  const getDefaultGroupSettings = (productCodes: string[]): DefaultGroupSetting[] =>
    defaultGroupSettings.filter(x =>
      productCodes.some(productCode => x.productCodes.includes(productCode))
    );

  const getProductCurriculumDefaultGroupSettings = (
    productCurriculumIds: number[]
  ): DefaultGroupSetting[] =>
    defaultGroupSettings.filter(x =>
      productCurriculumIds.some(productCurriculumId =>
        x.productCurriculumIds.includes(productCurriculumId)
      )
    );

  const getDefaultGroup = (productCodes: string[], gradeLevel: string): GroupCreate | null => {
    const productDefaultGroupSettings = getDefaultGroupSettings(productCodes);
    if (productDefaultGroupSettings.length === 0) return null;

    const groupSettings = productDefaultGroupSettings.find(x => x.gradeLevels.includes(gradeLevel));
    return groupSettings?.group ?? null;
  };

  const getProductCurriculumDefaultGroup = (
    productCurriculumIds: number[],
    gradeLevel: string
  ): GroupCreate | null => {
    const productDefaultGroupSettings = getProductCurriculumDefaultGroupSettings(
      productCurriculumIds
    );

    if (productDefaultGroupSettings.length === 0) return null;

    const groupSettings = productDefaultGroupSettings.find(x => x.gradeLevels.includes(gradeLevel));
    return groupSettings?.group ?? null;
  };

  const getGroupEnrollments = (
    classroomId: number,
    students: Student[],
    licenseActionInputs: BulkLicenseActionInput[]
  ): BulkGroupEnrollmentActionInput[] => {
    const enrollments: BulkGroupEnrollmentActionInput[] = [];

    students.forEach(student => {
      const studentClassroomGroup = student?.groups?.find(
        group => group.classroomId === classroomId
      );

      const productCurriculumIds = licenseActionInputs
        .filter(x => x.studentId === student.id && x.action === LicenseAction.ASSIGN)
        .map(x => x.productCurriculum!.id!);

      const defaultGroup = getProductCurriculumDefaultGroup(
        productCurriculumIds,
        student.gradeLevel
      );

      if (defaultGroup && !studentClassroomGroup) {
        enrollments.push({
          action: GroupEnrollmentAction.ENROLL,
          studentId: student?.id,
          group: {
            ...defaultGroup,
            classroomId,
            ...(defaultGroup.groupCategory && {
              groupCategory: { ...defaultGroup.groupCategory, classroomId }
            })
          }
        } as BulkGroupEnrollmentActionInput);
      }
    });

    return enrollments;
  };

  const getGroupUnenrollments = (
    classroomId: number,
    students: Student[],
    licenseActionInputs: BulkLicenseActionInput[]
  ): BulkGroupEnrollmentActionInput[] => {
    const unenrollments: BulkGroupEnrollmentActionInput[] = [];

    students.forEach(student => {
      const studentClassroomGroup = student?.groups?.find(isClassroomEqual(classroomId));

      const productCurriculumIds = licenseActionInputs
        .filter(x => x.studentId === student.id && x.action === LicenseAction.UNASSIGN)
        .map(x => x.productCurriculum!.id!);

      const defaultGroup = getProductCurriculumDefaultGroup(
        productCurriculumIds,
        student.gradeLevel
      );

      if (defaultGroup && studentClassroomGroup) {
        unenrollments.push({
          action: GroupEnrollmentAction.UNENROLL,
          studentId: student.id,
          groupId: studentClassroomGroup.id
        } as BulkGroupEnrollmentActionInput);
      }
    });

    return unenrollments;
  };

  return {
    getDefaultGroupSettings,
    getProductCurriculumDefaultGroupSettings,
    getDefaultGroup,
    getProductCurriculumDefaultGroup,
    getGroupEnrollments,
    getGroupUnenrollments
  };
};

export default useDefaultGroupSettings;
